/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import { useQuery } from '@apollo/client';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, InlineNotification, Link, TextField, Typography2 } from '@coursera/cds-core';

import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication/constants';
import Form from 'bundles/authentication/shared/Form';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import Redirection from 'bundles/authentication/shared/components/authentication-modal/OrganizationSSO/Redirection';
import type {
  OrgLoaderQuery,
  OrgLoaderQueryVariables,
} from 'bundles/authentication/shared/components/authentication-modal/OrganizationSSO/__generated__/OrgLoaderQuery';
import { OrganizationQuery } from 'bundles/authentication/shared/components/authentication-modal/OrganizationSSO/queries';

import _t from 'i18n!nls/authentication';

type Props = {
  error?: string;
  ssoMode?: Extract<AUTHENTICATION_MODAL_MODES_KEY, 'organizationsignup' | 'organizationlogin'>;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  onSubmit: ({ url }: { url: string }) => void;
  hideBackToLink?: boolean;
};

const styles = {
  container: css`
    display: grid;
    grid-gap: var(--cds-spacing-300);
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: var(--cds-spacing-400) var(--cds-spacing-200);
  `,
  header: css`
    text-align: center;
  `,
  form: css`
    display: grid;
    grid-gap: var(--cds-spacing-200);
  `,
  navigation: css`
    margin: 0;
    text-align: center;
  `,
  submit: css`
    margin-top: var(--cds-spacing-200);
  `,
  inputSection: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
};

export const OrganizationSSO = (props: Props) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [url, setUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);

  const navigateBack = () => {
    const { navigate } = props;

    const navigateTarget =
      props.ssoMode === AUTHENTICATION_MODAL_MODES.ORGANIZATIONSIGNUP
        ? AUTHENTICATION_MODAL_MODES.SIGNUP
        : AUTHENTICATION_MODAL_MODES.LOGIN;

    return navigate(navigateTarget);
  };

  const { ssoMode, hideBackToLink = false } = props;

  const { data, error, loading } = useQuery<OrgLoaderQuery, OrgLoaderQueryVariables>(OrganizationQuery, {
    skip: !url,
    variables: { id: url || '' },
  });

  const title =
    ssoMode === AUTHENTICATION_MODAL_MODES.ORGANIZATIONSIGNUP
      ? _t('Sign up with your organization')
      : _t('Log in with your organization');
  const navigateTo = ssoMode === AUTHENTICATION_MODAL_MODES.ORGANIZATIONSIGNUP ? _t('Sign Up') : _t('Log in');

  const id = data?.ThirdPartyOrganizationEmailDomainsV1Resource?.get?.thirdPartyOrganizationId;

  if (!loading && id) {
    return <Redirection {...props} id={id} />;
  }

  if (!loading && (data || error)) {
    setHasError(true);
    setUrl(null);
  }

  return (
    <section className="rc-OrganizationSSO" css={styles.container}>
      <header css={styles.header}>
        <Typography2 variant="titleMedium" component="h1">
          {title}
        </Typography2>
      </header>
      <Form
        css={styles.form}
        onSubmit={({ url }) => setUrl(url)}
        onInput={(e) => {
          const formRef = e.currentTarget;
          setIsButtonDisabled(!formRef.checkValidity());
        }}
      >
        {hasError && (
          <InlineNotification severity="error" title={_t('Organization not found')}>
            {_t('Please enter a valid organization and ensure it is formatted correctly')}
          </InlineNotification>
        )}
        <div css={styles.inputSection}>
          <TextField
            autoComplete="organization"
            label={_t('Enter Your Organization URL')}
            name="url"
            placeholder={_t('(ex: company.com or company.org)')}
          />
          <Link
            typographyVariant="bodySecondary"
            href="https://learner.coursera.help/hc/articles/115004807263"
            rel="noopener noreferrer"
            target="_blank"
          >
            {_t('Help')}
          </Link>
        </div>

        <Button fullWidth={true} disabled={isButtonDisabled} type="submit" css={styles.submit}>
          Continue
        </Button>
      </Form>

      {!hideBackToLink && (
        <p css={styles.navigation}>
          <FormattedMessage
            message={_t('{backTo} {backToLink}.')}
            backTo={<span aria-hidden="true">{_t('Back to')}</span>}
            backToLink={
              <LinkButton onClick={navigateBack} type="button">
                {navigateTo}
              </LinkButton>
            }
          />
        </p>
      )}
    </section>
  );
};

export default OrganizationSSO;
